import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import useFeatureCheck from "../../../customHooks/useFeatureCheck";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Col, DatePicker, Input, message, Row, Select } from "antd";

const { TextArea } = Input;
import { DeleteFilled } from "@ant-design/icons";
import moment from "moment";
import { INPUT_SIZE } from "../../../common/ThemeConstants";
import FormSelect from "../../../components/general/FormSelect";
import FormTextField from "../../../components/general/FormTextField";
import { postRequest } from "../../../services/mainApp.service";
import { clampQtyRateValue } from "../../../utils/commonfunction";
import { PHYSICAL_STOCK } from "../../../common/InventoryEnum";
import BasicFormComponentInvTransaction from "../../../components/formComponent/BasicFormComponentInvTransaction";

const initialFormValues = {
  TransactionMasterId: null,
  TransactionNo: "",
  TransactionDate: moment().format("YYYY-MM-DD"),
  ReferenceNo: "",
  Description: "",
  TransactionTypeId: PHYSICAL_STOCK,
  BranchId: null,
  DepartmentId: null,
  DemandMasterId: null,
  IsEnable: true,
  IsSubmit: false,
  TransactionDetails: [],
};

const CreateEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const controller = new window.AbortController();
  const userData = useSelector((state) => state.authReducer);
  const [tableLoading, setTableLoading] = useState(false);
  const isEditMode = !!id;
  const [formFields, setFormFields] = useState(initialFormValues);
  const [formLoading, setFormLoading] = useState(false);
  const [supportingTable, setSupportingTable] = useState({});
  const [transactionDetails, setTransactionDetails] = useState([]);
  const [selectedItemForAdd, setSelectedItemForAdd] = useState(null);
  const [addDetailQty, setAddDetailQty] = useState("");
  const itemSelectRef = useRef(null);
  const itemsFetchedRef = useRef(false);
  const checkFeatureAuth = useFeatureCheck();

  useEffect(() => {
    fetchInitialData();

    return () => {
      controller.abort();
    };
  }, [id]);

  useEffect(() => {
    const branches = supportingTable?.Branches || [];
    const departments = supportingTable?.Departments || [];
    setFormFields((prev) => {
      let next = { ...prev };
      if (branches.length === 1 && (prev.BranchId == null || prev.BranchId === "")) {
        next.BranchId = branches[0].Id;
      }
      if (departments.length === 1 && (prev.DepartmentId == null || prev.DepartmentId === "")) {
        next.DepartmentId = departments[0].Id;
      }
      return next;
    });
  }, [supportingTable?.Branches, supportingTable?.Departments]);

  // Fetch items when both Branch and Department are selected
  useEffect(() => {
    if (formFields.BranchId && formFields.DepartmentId && supportingTable?.Branches?.length > 0) {
      // Only fetch if items haven't been fetched yet or if branch/department changed
      const currentKey = `${formFields.BranchId}-${formFields.DepartmentId}`;
      if (!itemsFetchedRef.current || itemsFetchedRef.current !== currentKey) {
        fetchItems();
        itemsFetchedRef.current = currentKey;
      }
    } else {
      // Reset items when branch or department is cleared
      setSupportingTable((prev) => ({
        ...prev,
        Items: [],
      }));
      itemsFetchedRef.current = null;
    }
  }, [formFields.BranchId, formFields.DepartmentId, supportingTable?.Branches]);

  const fetchInitialData = async () => {
    setTableLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionTypeId: PHYSICAL_STOCK,
      TransactionMasterId: isEditMode ? parseInt(id) : null,
      IsDetailData: isEditMode ? true : false,
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setTableLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage || "Error fetching data"
        );
        return;
      }

      const dataSet = response.data;

      if (isEditMode) {

        const masterData = dataSet?.Data?.[0] || {};
        setFormFields((prev) => ({ ...prev, ...masterData }));

        const detailsData = masterData?.TransactionDetails || [];
        // Ensure RateIU is set to LastPurchaseRateIU for all details
        const normalizedDetails = detailsData.map((detail) => ({
          ...detail,
          RateIU: detail.LastPurchaseRateIU ?? detail.RateIU ?? 0,
        }));
        setTransactionDetails(normalizedDetails);
      }

      // Set supporting tables - only Branches, Departments, and TransactionNo initially
      if (dataSet.AdditionalData || dataSet.DataSet) {
        setSupportingTable({
          Branches: dataSet.AdditionalData?.Branches || dataSet.DataSet?.Table1 || [],
          Departments: dataSet.AdditionalData?.Departments || dataSet.DataSet?.Table2 || [],
          TransactionNo: dataSet?.AdditionalData?.TransactionCode?.[0]?.Code || "",
          Items: [], // Don't load items initially
        });
      }

    } catch (error) {
      setTableLoading(false);
      if (error.message !== "canceled") {
        console.error("Error fetching transactions:", error);
        message.error("Failed to fetch transactions");
      }
    }
  };

  const fetchItems = async () => {
    if (!formFields.BranchId || !formFields.DepartmentId) {
      return;
    }

    setTableLoading(true);
    const payload = {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionTypeId: PHYSICAL_STOCK,
      BranchId: formFields.BranchId,
      DepartmentId: formFields.DepartmentId,
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setTableLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage || "Error fetching items"
        );
        return;
      }

      const dataSet = response.data;

      // Update Items in supporting table
      if (dataSet.AdditionalData || dataSet.DataSet) {
        setSupportingTable((prev) => ({
          ...prev,
          Items: dataSet.AdditionalData?.Items || dataSet.DataSet?.Table3 || [],
        }));
      }

    } catch (error) {
      setTableLoading(false);
      if (error.message !== "canceled") {
        console.error("Error fetching items:", error);
        message.error("Failed to fetch items");
      }
    }
  };

  const handleFormChange = (data) => {
    setFormFields((prev) => {
      const updated = { ...prev, [data.name]: data.value };

      // If Branch or Department is cleared, clear items
      if (data.name === "BranchId" || data.name === "DepartmentId") {
        if (!updated.BranchId || !updated.DepartmentId) {
          setSupportingTable((prev) => ({
            ...prev,
            Items: [],
          }));
          setSelectedItemForAdd(null);
          itemsFetchedRef.current = null;
        }
      }

      return updated;
    });
  };

  const handleDateChange = (date) => {
    setFormFields((prev) => ({
      ...prev,
      TransactionDate: date ? date.format("YYYY-MM-DD") : null,
    }));
  };

  const itemList = supportingTable?.Items || [];

  // Calculate Last Purchase Rate in Issuance Unit
  const calculateLastPurchaseRateIU = (item) => {
    const lastPurchaseRatePU = item?.LastPurchaseRatePU ?? 0;
    const purchaseIssuanceConversion = item?.PurchaseIssuanceConversion ?? 1;
    if (purchaseIssuanceConversion > 0) {
      return lastPurchaseRatePU / purchaseIssuanceConversion;
    }
    return lastPurchaseRatePU;
  };

  const handleAddDetail = () => {
    const qty = clampQtyRateValue(addDetailQty);

    if (selectedItemForAdd) {
      const itemId = selectedItemForAdd.ItemId ?? selectedItemForAdd.Id;
      const isDuplicate = transactionDetails.some(
        (d) => (d.ItemId ?? d.Id) != null && (d.ItemId ?? d.Id) === itemId
      );
      if (isDuplicate) {
        message.error("This item is already added to the table. Duplicate items are not allowed.");
        return;
      }

      // Validate quantity against available stock in Issuance Unit
      const stockIU = selectedItemForAdd.StockIU ?? 0;
      if (qty > stockIU) {
        message.error(`Quantity cannot exceed available stock. Available stock: ${stockIU.toFixed(2)} ${selectedItemForAdd.IssuanceUnit ?? selectedItemForAdd.UnitName ?? ""}`);
        return;
      }

      if (qty <= 0) {
        message.error("Quantity must be greater than zero.");
        return;
      }

      const lastPurchaseRateIU = clampQtyRateValue(calculateLastPurchaseRateIU(selectedItemForAdd));

      const newDetail = {
        ItemId: itemId,
        ItemName: selectedItemForAdd.ItemName ?? selectedItemForAdd.Name,
        PurchaseUnitId: selectedItemForAdd.PurchaseUnitId ?? selectedItemForAdd.UnitId ?? null,
        PurchaseUnit: selectedItemForAdd.PurchaseUnit ?? selectedItemForAdd.UnitName ?? "",
        QtyPU: 0,
        RatePU: 0,
        IssuanceUnitId: selectedItemForAdd.IssuanceUnitId ?? selectedItemForAdd.UnitId ?? null,
        IssuanceUnit: selectedItemForAdd.IssuanceUnit ?? selectedItemForAdd.UnitName ?? "",
        QtyIU: qty,
        RateIU: lastPurchaseRateIU, // Always use Last Purchase Rate in Issuance Unit
        RecipeUnitId: null,
        QtyRU: 0,
        RateRU: 0,
        StockIU: selectedItemForAdd.StockIU ?? 0,
        StockPU: selectedItemForAdd.StockPU ?? 0,
        IsEnable: true,
        LastPurchaseRatePU: selectedItemForAdd.LastPurchaseRatePU ?? 0,
        LastPurchaseRateIU: lastPurchaseRateIU,
        PurchaseIssuanceConversion: selectedItemForAdd.PurchaseIssuanceConversion ?? 1,
      };
      setTransactionDetails([newDetail, ...transactionDetails]);
      setSelectedItemForAdd(null);
      setAddDetailQty("");
      setTimeout(() => itemSelectRef.current?.focus(), 0);
    }
  };

  const handleDetailChange = (index, field, value) => {
    // Validate quantity against available stock when QtyIU is changed
    if (field === "QtyIU") {
      value = clampQtyRateValue(value);
      const qty = value;
      const currentDetail = transactionDetails[index]; // Get original detail before update
      const stockIU = currentDetail.StockIU ?? 0;
      const currentQtyIU = currentDetail.QtyIU ?? 0; // Original quantity before change

      // StockIU represents the stock before this transaction was created
      // When editing, we need to account for the quantity being replaced
      // Available stock = StockIU (original stock) + currentQtyIU (quantity we're replacing)
      const availableStock = stockIU + currentQtyIU;

      if (qty > availableStock) {
        message.error(`Quantity cannot exceed available stock. Available stock: ${availableStock.toFixed(2)} ${currentDetail.IssuanceUnit ?? ""}`);
        return; // Don't update if validation fails
      }

      if (qty <= 0) {
        message.error("Quantity must be greater than zero.");
        return; // Don't update if validation fails
      }
    }

    const updatedDetails = [...transactionDetails];
    updatedDetails[index] = {
      ...updatedDetails[index],
      [field]: value,
    };

    // Calculate totals if needed
    if (field === "QtyIU") {
      updatedDetails[index].QtyIU = updatedDetails[index].QtyIU || 0;
    }

    setTransactionDetails(updatedDetails);
  };

  const handleRemoveDetail = (index) => {
    const updatedDetails = transactionDetails.filter((_, i) => i !== index);
    setTransactionDetails(updatedDetails);
  };

  const handleFormSave = async (e, operationId, isSubmitFlag) => {
    e.preventDefault();

    // Check POST feature permission when submitting
    if (isSubmitFlag && !checkFeatureAuth("POST", "PhysicalStock")) {
      return;
    }

    if (!formFields.BranchId) {
      message.error("Please select a branch");
      return;
    }

    if (!formFields.DepartmentId) {
      message.error("Please select a department");
      return;
    }

    if (transactionDetails.length === 0) {
      message.error("Please add at least one transaction detail");
      return;
    }

    // Validate stock quantity on Submit
    if (isSubmitFlag) {

      const stockInvalidItems = transactionDetails.filter((detail) =>
        detail.StockIU !== undefined &&
        parseFloat(detail.QtyIU) > parseFloat(detail.StockIU));

      if (stockInvalidItems.length > 0) {
        const invalidItemDetails = stockInvalidItems
          .map((item) => {
            return `${item.ItemName || "Unknown Item"} (Quantity: ${item.QtyIU.toFixed(2)}, Available Stock: ${item.StockIU.toFixed(2)} ${item.IssuanceUnit || ""})`;
          })
          .join("; ");
        message.error(
          `Quantity exceeds available stock for the following items: ${invalidItemDetails}. Please adjust the quantities before submitting.`
        );
        return;
      }
    }

    setFormLoading(true);

    const payload = {
      OperationId: operationId, // 2 for create, 3 for update
      CompanyId: userData.CompanyId,
      UserId: userData.UserId,
      UserIP: "12.1.1.2",
      TransactionMasterId: isEditMode ? parseInt(id) : null,
      TransactionNo: formFields.TransactionNo,
      TransactionDate: formFields.TransactionDate,
      ReferenceNo: formFields.ReferenceNo,
      Description: formFields.Description ?? "",
      TransactionTypeId: PHYSICAL_STOCK,
      BranchId: formFields.BranchId,
      DepartmentId: formFields.DepartmentId,
      Gross: 0,
      Tax: 0,
      Discount: 0,
      Amount: 0,
      VendorId: null, // No vendor for physical stock
      DemandMasterId: formFields.DemandMasterId || null,
      IsEnable: formFields.IsEnable,
      IsSubmit: isSubmitFlag || formFields.IsSubmit,
      TransactionDetails: transactionDetails.map((detail) => ({
        ItemId: detail.ItemId,
        PurchaseUnitId: detail.PurchaseUnitId || null,
        QtyPU: 0,
        RatePU: 0,
        IssuanceUnitId: detail.IssuanceUnitId || null,
        QtyIU: detail.QtyIU || 0,
        RateIU: detail.RateIU || 0,
        RecipeUnitId: detail.RecipeUnitId || null,
        QtyRU: 0,
        RateRU: 0,
        IsEnable: detail.IsEnable !== false,
      })),
    };

    try {
      const response = await postRequest("/Inv_Transaction", payload, controller);
      setFormLoading(false);

      if (response.error) {
        message.error(response.errorMessage);
        return;
      }

      if (response.data.response === false) {
        message.error(
          response.data.DataSet?.Table?.errorMessage ||
          `Error ${isEditMode ? "updating" : "creating"} transaction`
        );
        return;
      }

      message.success(
        response.successMessage ||
        `Transaction ${isEditMode ? "updated" : "created"} successfully`
      );
      navigate("/physicalstock");
    } catch (error) {
      setFormLoading(false);
      if (error.message !== "canceled") {
        console.error("Error saving transaction:", error);
        const apiError =
          error.response?.data?.Error_Message ||
          error.response?.data?.Message ||
          error.response?.data?.errorMessage ||
          error.response?.data?.ResponseMessage;
        message.error(
          apiError || `Failed to ${isEditMode ? "update" : "create"} physical stock`
        );
      }
    }
  };

  const detailColumns = useMemo(
    () => [
      { title: "Item Name", dataIndex: "ItemName", key: "ItemName" },
      { title: "Issuance Unit", dataIndex: "IssuanceUnit", key: "IssuanceUnit" },
      {
        title: "Qty",
        dataIndex: "QtyIU",
        key: "QtyIU",
        width: 100,
        render: (_, record, index) => {
          const stockIU = record.StockIU ?? 0;
          const currentQtyIU = record.QtyIU ?? 0;
          const availableStock = stockIU + currentQtyIU; // Account for quantity being replaced in edit mode
          return (
            <Input
              type="number"
              value={record.QtyIU}
              onChange={(e) =>
                handleDetailChange(index, "QtyIU", parseFloat(e.target.value) || 0)
              }
              style={{ width: "100%" }}
              readOnly={formFields.IsSubmit}
              min={0}
              max={availableStock}
              step={0.01}
            />
          );
        },
      },
      ...(!formFields.IsSubmit
        ? [
          {
            title: "Action",
            key: "actions",
            width: 100,
            render: (_, record, index) => (
              <Button
                type="link"
                danger
                icon={<DeleteFilled />}
                onClick={() => handleRemoveDetail(index)}
              >
              </Button>
            ),
          },
        ]
        : []),
    ],
    [formFields.IsSubmit, handleDetailChange, handleRemoveDetail]
  );

  const labelStyle = { display: "block", marginBottom: 6, fontWeight: 500 };

  const formPanel = (
    <Fragment>

      <Col xs={24} sm={12} md={4}>
        <FormTextField
          colSpan={24}
          label="Transaction No"
          name="TransactionNo"
          size={INPUT_SIZE}
          value={isEditMode ? formFields.TransactionNo : supportingTable?.TransactionNo}
          readOnly={true}
        />
      </Col>
      <Col xs={24} sm={12} md={4}>
        <label style={labelStyle}>
          Transaction Date <span style={{ color: "#ff4d4f" }}>*</span>
        </label>
        {!formFields.IsSubmit ? (
          <DatePicker
            style={{ width: "100%" }}
            value={formFields.TransactionDate ? moment(formFields.TransactionDate) : null}
            onChange={handleDateChange}
            format="YYYY-MM-DD"
          />
        ) : (
          <FormTextField
            name="TransactionDate"
            size={INPUT_SIZE}
            value={formFields.TransactionDate ? moment(formFields.TransactionDate).format("YYYY-MM-DD") : null}
            readOnly={true}
          />
        )}
      </Col>
      <Col xs={24} sm={12} md={4}>
        <FormSelect
          colSpan={24}
          listItem={supportingTable.Branches || []}
          idName="Id"
          valueName="Name"
          size={INPUT_SIZE}
          name="BranchId"
          label="Branch"
          value={formFields.BranchId}
          onChange={handleFormChange}
          required={true}
          disabled={formFields.IsSubmit}
        />
      </Col>
      <Col xs={24} sm={12} md={4}>
        <FormSelect
          colSpan={24}
          listItem={supportingTable.Departments || []}
          idName="Id"
          valueName="Name"
          size={INPUT_SIZE}
          name="DepartmentId"
          label="Department"
          value={formFields.DepartmentId}
          onChange={handleFormChange}
          required={true}
          disabled={formFields.IsSubmit}
        />
      </Col>
      <Col span={24}>
        <label style={labelStyle}>Description</label>
        <TextArea
          name="Description"
          value={formFields.Description ?? ""}
          onChange={(e) => handleFormChange({ name: "Description", value: e.target.value })}
          rows={1}
          placeholder="Enter description (optional)"
          readOnly={formFields.IsSubmit}
          disabled={formFields.IsSubmit}
          style={{ width: "100%" }}
        />
      </Col>
      <Col xs={24} sm={12} md={6}>
        <FormTextField
          colSpan={24}
          label="Total Items"
          name="TotalItems"
          size={INPUT_SIZE}
          value={transactionDetails.length}
          readOnly={true}
          type="text"
          isNumber="true"
        />
      </Col>

    </Fragment>
  );

  const getItemId = (item) => item?.ItemId ?? item?.Id;
  const getItemName = (item) => item?.ItemName + " ( " + (item?.IssuanceUnit ?? item?.UnitName ?? "") + " )";
  const getItemCode = (item) => item?.ItemCode ?? item?.Code ?? "";
  const getBarcode = (item) => item?.Barcode ?? item?.BarCode ?? "";
  const getStockIU = (item) => item?.StockIU ?? item?.Stock ?? 0;

  const addDetailEntryPanel = (
    <>
      {!formFields.IsSubmit && (
        <div
          style={{
            width: "100%",
            padding: "16px",
            background: "#fafafa",
            borderRadius: 8,
            border: "1px solid #f0f0f0",
          }}
        >
          <div
            style={{
              marginBottom: 12,
              fontSize: 14,
              fontWeight: 600,
              color: "#262626",
            }}
          >
            Add item to physical stock
          </div>
          <Row gutter={[16, 12]} align="bottom">
            <Col xs={24} sm={10} md={4} lg={3}>
              <label style={labelStyle}>Bar code</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Bar code"
                showSearch
                allowClear
                size="middle"
                disabled={!formFields.BranchId || !formFields.DepartmentId}
                value={selectedItemForAdd ? getBarcode(selectedItemForAdd) : undefined}
                onChange={(value) => {
                  const item = itemList.find((i) => getBarcode(i) === value);
                  setSelectedItemForAdd(item || null);
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  ...itemList
                    .filter((i) => getBarcode(i))
                    .map((item) => ({
                      value: getBarcode(item),
                      label: getBarcode(item),
                    })),
                ]}
              />
            </Col>
            <Col xs={24} sm={10} md={4} lg={3}>
              <label style={labelStyle}>Item code</label>
              <Select
                style={{ width: "100%" }}
                placeholder="Item code"
                showSearch
                allowClear
                size="middle"
                disabled={!formFields.BranchId || !formFields.DepartmentId}
                value={selectedItemForAdd ? getItemCode(selectedItemForAdd) : undefined}
                onChange={(value) => {
                  const item = itemList.find((i) => getItemCode(i) === value);
                  setSelectedItemForAdd(item || null);
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  ...itemList
                    .filter((i) => getItemCode(i))
                    .map((item) => ({
                      value: getItemCode(item),
                      label: getItemCode(item),
                    })),
                ]}
              />
            </Col>
            <Col xs={24} sm={12} md={6} lg={5}>
              <label style={labelStyle}>Item name</label>
              <Select
                ref={itemSelectRef}
                style={{ width: "100%" }}
                placeholder={
                  !formFields.BranchId || !formFields.DepartmentId
                    ? "Select Branch and Department first"
                    : "Search by item name"
                }
                showSearch
                allowClear
                size="middle"
                disabled={!formFields.BranchId || !formFields.DepartmentId}
                value={selectedItemForAdd ? getItemId(selectedItemForAdd) : undefined}
                onChange={(value) => {
                  const item = itemList.find((i) => getItemId(i) === value);
                  setSelectedItemForAdd(item || null);
                }}
                filterOption={(input, option) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={[
                  ...itemList.map((item) => ({
                    value: getItemId(item),
                    label: getItemName(item),
                  })),
                ]}
              />
            </Col>
            <Col xs={12} sm={8} md={4} lg={3}>
              <label style={labelStyle}>Quantity (IU)</label>
              <Input
                type="number"
                placeholder="0"
                size="middle"
                value={addDetailQty}
                onChange={(e) => {
                  const value = e.target.value;
                  setAddDetailQty(value);
                  // Validate in real-time
                  if (selectedItemForAdd && value) {
                    const qty = parseFloat(value) || 0;
                    const stockIU = getStockIU(selectedItemForAdd);
                    if (qty > stockIU) {
                      message.warning(`Quantity exceeds available stock (${stockIU.toFixed(2)})`, 2);
                    }
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleAddDetail();
                  }
                }}
                min={0}
                max={selectedItemForAdd ? getStockIU(selectedItemForAdd) : undefined}
                step={0.01}
                disabled={!formFields.BranchId || !formFields.DepartmentId}
              />
            </Col>
          </Row>
        </div>
      )}
    </>
  );

  return (
    <BasicFormComponentInvTransaction
      pageMode="transaction"
      isEditMode={isEditMode}
      formTitle={"Physical Stock" + (formFields.IsSubmit ? " - ( Posted )" : "")}
      crudTitle={"Physical Stock" + (formFields.IsSubmit ? " - ( Posted )" : "")}
      baseRoute="/physicalstock"
      formPanel={formPanel}
      transactionDetailsColumns={detailColumns}
      transactionDetailsRows={transactionDetails}
      onAddDetail={handleAddDetail}
      onFormSave={handleFormSave}
      showSubmit={!formFields.IsSubmit && checkFeatureAuth("POST", "PhysicalStock", { showErrorMessage: false })}
      formLoading={formLoading}
      submitFormLoading={formLoading}
      onCancelForm={() => navigate("/physicalstock")}
      addDetailEntryPanel={addDetailEntryPanel}
      showSave={!formFields.IsSubmit}
      showAddDetailButton={!formFields.IsSubmit}
    />
  );
};

export default CreateEdit;
